import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import type {
  FileNormalizationAnalyzeResult,
} from '../types';

type FileNormalizationReviewPanelProps = {
  result: FileNormalizationAnalyzeResult;
  selectedSheetName: string;
  selectedHeaderRowIndex: number;
  isReviewing?: boolean;
  isContinuing?: boolean;
  onSheetChange: (sheetName: string) => void;
  onHeaderRowChange: (rowIndex: number) => void;
  onContinue: () => void;
};

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const text = String(value).trim();

  if (!text) {
    return '—';
  }

  return text.length > 42
    ? `${text.slice(0, 42)}…`
    : text;
}

function sourceLabel(
  source: FileNormalizationAnalyzeResult['source'],
) {
  if (source === 'profile') {
    return 'Configuración recordada';
  }

  if (source === 'manual_review') {
    return 'Configuración ajustada';
  }

  return 'Detección automática';
}

export default function FileNormalizationReviewPanel({
  result,
  selectedSheetName,
  selectedHeaderRowIndex,
  isReviewing = false,
  isContinuing = false,
  onSheetChange,
  onHeaderRowChange,
  onContinue,
}: FileNormalizationReviewPanelProps) {
  const hasWarnings = result.warnings.length > 0;

  const normalizedSelectedSheet =
    selectedSheetName || result.selected_sheet || '';

  return (
    <Stack spacing={1.5}>
      <Alert
        severity={hasWarnings ? 'warning' : 'info'}
      >
        <Stack spacing={0.55}>
          <Typography sx={{ fontWeight: 850 }}>
            {sourceLabel(result.source)}
          </Typography>

          <Typography variant="body2">
            {normalizedSelectedSheet
              ? `Hoja: ${normalizedSelectedSheet} · `
              : ''}
            Cabecera seleccionada: fila{' '}
            {selectedHeaderRowIndex + 1}
          </Typography>

          <Typography variant="body2">
            {result.source === 'profile'
              ? 'PROA ha recuperado la configuración guardada. Comprueba visualmente la fila marcada o continúa.'
              : 'Comprueba la estructura. Si la cabecera propuesta no es correcta, haz clic sobre la fila correcta.'}
          </Typography>

          {result.warnings.map((warning, index) => (
            <Typography
              key={`${warning.code}-${index}`}
              variant="body2"
            >
              {warning.message}
            </Typography>
          ))}
        </Stack>
      </Alert>

      <Stack
        spacing={1.5}
        sx={{
          p: 2,
          border:
            '1px solid rgba(148,163,184,0.18)',
          borderRadius: 2,
          bgcolor: 'rgba(15,23,42,0.44)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {(result.sheets ?? []).length > 0 ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Hoja del archivo"
              value={normalizedSelectedSheet}
              onChange={(event) =>
                onSheetChange(event.target.value)
              }
              disabled={isReviewing || isContinuing}
              sx={{ maxWidth: { md: 420 } }}
            >
              {result.sheets.map((sheet) => (
                <MenuItem
                  key={sheet.name}
                  value={sheet.name}
                >
                  {sheet.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              Selecciona la fila de encabezados
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: '#94a3b8' }}
            >
              No escribas un número: pulsa directamente
              sobre la fila correcta.
            </Typography>
          </Box>
        </Stack>

        {isReviewing ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 2 }}
          >
            <CircularProgress size={18} />
            <Typography
              variant="body2"
              sx={{ color: '#94a3b8' }}
            >
              Cargando muestra de la hoja...
            </Typography>
          </Stack>
        ) : (
          <TableContainer
            sx={{
              maxHeight: 390,
              overflowX: 'auto',
              border:
                '1px solid rgba(148,163,184,0.16)',
              borderRadius: 1.5,
              bgcolor: 'rgba(2,6,23,0.28)',
            }}
          >
            <Table size="small" stickyHeader>
              <TableBody>
                {(result.raw_preview_rows ?? []).map(
                  (row, rowIndex) => {
                    const selected =
                      selectedHeaderRowIndex === rowIndex;

                    return (
                      <TableRow
                        key={`normalization-row-${rowIndex}`}
                        selected={selected}
                        hover
                        onClick={() =>
                          onHeaderRowChange(rowIndex)
                        }
                        sx={{
                          cursor: 'pointer',
                          '& td': {
                            borderColor:
                              'rgba(148,163,184,0.10)',
                          },
                          ...(selected
                            ? {
                                '& td': {
                                  bgcolor:
                                    'rgba(56,189,248,0.16)',
                                  color: '#e0f2fe',
                                  fontWeight: 800,
                                  borderColor:
                                    'rgba(56,189,248,0.24)',
                                },
                              }
                            : {}),
                        }}
                      >
                        <TableCell
                          sx={{
                            minWidth: 110,
                            whiteSpace: 'nowrap',
                            color: selected
                              ? '#38bdf8'
                              : '#64748b',
                            fontWeight: 850,
                          }}
                        >
                          {selected
                            ? 'CABECERA ✓'
                            : `Fila ${rowIndex + 1}`}
                        </TableCell>

                        {row.map(
                          (cell, cellIndex) => (
                            <TableCell
                              key={`normalization-cell-${rowIndex}-${cellIndex}`}
                              sx={{
                                whiteSpace: 'nowrap',
                                color: selected
                                  ? '#e0f2fe'
                                  : '#cbd5e1',
                                py: 0.75,
                              }}
                            >
                              {formatCell(cell)}
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#64748b' }}
          >
            Esta vista solo lee una muestra del archivo.
            Todavía no se están importando datos.
          </Typography>

          <Button
            variant="contained"
            onClick={onContinue}
            disabled={isReviewing || isContinuing}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              whiteSpace: 'nowrap',
            }}
          >
            {isContinuing
              ? 'Guardando...'
              : 'Guardar estructura y continuar'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
